/**
 * Streaming Chat Integration
 * Handles OpenAI streaming responses using Server-Sent Events
 */

class StreamingChatManager {
    constructor() {
        this.currentStream = null;
        this.currentRequestId = null;
        this.isStreaming = false;
        this.onChunk = null;
        this.onComplete = null;
        this.onError = null;
        this.onMetadata = null;
    }

    /**
     * Start a streaming chat conversation
     * @param {string} message - The user's message
     * @param {Array} conversationHistory - Previous conversation history
     * @param {Object} callbacks - Callback functions for different events
     */
    async startStream(message, conversationHistory = [], callbacks = {}) {
        // Set up callbacks
        this.onChunk = callbacks.onChunk || ((chunk) => console.log('Chunk:', chunk));
        this.onComplete = callbacks.onComplete || ((data) => console.log('Complete:', data));
        this.onError = callbacks.onError || ((error) => console.error('Error:', error));
        this.onMetadata = callbacks.onMetadata || ((metadata) => console.log('Metadata:', metadata));

        // Stop any existing stream
        this.stopStream();

        // Set streaming state
        this.isStreaming = true;

        try {
            // Create EventSource for streaming
            const eventSource = new EventSource('/api/chat/stream', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    message: message,
                    conversation_history: conversationHistory
                })
            });

            // However, EventSource doesn't support POST, so we'll use fetch with stream instead
            const response = await fetch('/api/chat/stream', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    message: message,
                    conversation_history: conversationHistory
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Store the response for potential cancellation
            this.currentStream = response;

            // Read the stream
            const reader = response.body.getReader();
            const decoder = new TextDecoder();

            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                
                if (done) {
                    this.isStreaming = false;
                    break;
                }

                // Decode the chunk
                buffer += decoder.decode(value, { stream: true });

                // Process complete lines
                const lines = buffer.split('\n');
                buffer = lines.pop(); // Keep the incomplete line in buffer

                for (const line of lines) {
                    if (line.trim() === '') continue;
                    
                    // Parse Server-Sent Events format
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.substring(6));
                            this.handleStreamData(data);
                        } catch (e) {
                            // Log truncated line to avoid console spam with large JSON
                            const truncatedLine = line.length > 200 ? line.substring(0, 200) + '...[Truncated]' : line;
                            console.warn('Failed to parse streaming data:', truncatedLine);
                        }
                    }
                }
            }

        } catch (error) {
            console.error('Streaming error:', error);
            this.isStreaming = false;
            this.onError(error);
        }
    }

    /**
     * Handle incoming stream data
     * @param {Object} data - The parsed stream data
     */
    handleStreamData(data) {
        switch (data.type) {
            case 'metadata':
                this.currentRequestId = data.request_id;
                this.onMetadata(data);
                break;
            
            case 'chunk':
                this.onChunk(data);
                break;
            
            case 'complete':
                this.isStreaming = false;
                this.onComplete(data);
                break;
            
            case 'error':
                this.isStreaming = false;
                this.onError(data);
                break;
            
            default:
                console.warn('Unknown stream data type:', data.type);
        }
    }

    /**
     * Stop the current stream
     */
    stopStream() {
        if (this.currentStream) {
            this.currentStream.body.cancel();
            this.currentStream = null;
        }
        this.isStreaming = false;
        this.currentRequestId = null;
    }

    /**
     * Get the current request ID
     * @returns {string|null} The current request ID
     */
    getCurrentRequestId() {
        return this.currentRequestId;
    }

    /**
     * Check if currently streaming
     * @returns {boolean} True if streaming is active
     */
    getIsStreaming() {
        return this.isStreaming;
    }
}

// Example usage function
function createStreamingChatExample() {
    const streamingChat = new StreamingChatManager();
    
    // Example callbacks
    const callbacks = {
        onMetadata: (metadata) => {
            console.log('Stream started with request ID:', metadata.request_id);
            console.log('Using model:', metadata.model);
        },
        
        onChunk: (chunk) => {
            // Update UI with new chunk
            console.log('Received chunk:', chunk.content);
            // You can append chunk.content to your chat interface here
        },
        
        onComplete: (data) => {
            console.log('Stream completed!');
            console.log('Full response:', data.complete_response);
            console.log('Detected tone:', data.tone);
            console.log('Audio status:', data.audio_status);
            console.log('Keyword images:', data.keyword_images);
        },
        
        onError: (error) => {
            console.error('Stream error:', error);
        }
    };

    // Start streaming
    streamingChat.startStream(
        "Hello! Tell me about quantum physics.",
        [], // No conversation history
        callbacks
    );
    
    return streamingChat;
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
    module.exports = { StreamingChatManager };
}

// Make available globally
window.StreamingChatManager = StreamingChatManager;
window.createStreamingChatExample = createStreamingChatExample;